<template>
  <section
    class="filter-bar border rounded bg-body-emphasis p-3"
    aria-label="Activity filters"
  >
    <CompanySearchDropdown
      :modelValue="model.company_id"
      :withLabel="false"
      :withDescription="false"
      placeholder="All companies"
      @update:modelValue="update({ company_id: $event ?? undefined })"
    />
    <AccountSearchDropdown
      :modelValue="model.account_id"
      @update:modelValue="update({ account_id: $event ?? undefined })"
    />
    <MultiSelect
      :modelValue="model.activity_keys"
      :options="keyOptions"
      filter
      placeholder="All activities"
      :maxSelectedLabels="1"
      selectedItemsLabel="{0} activities"
      aria-label="Activities"
      @update:modelValue="update({ activity_keys: $event })"
    />
    <input
      v-for="field in idFields"
      :key="field.key"
      class="form-control form-control-sm"
      type="number"
      min="1"
      step="1"
      :value="model[field.key] ?? ''"
      :aria-label="field.label"
      :placeholder="field.label"
      @change="updateId(field.key, ($event.target as HTMLInputElement).value)"
    />
    <label class="small"
      >From (UTC)<input
        class="form-control form-control-sm"
        type="datetime-local"
        :value="model.created_from?.slice(0, 16)"
        @change="
          updateInstant(
            'created_from',
            ($event.target as HTMLInputElement).value
          )
        "
    /></label>
    <label class="small"
      >Until (UTC)<input
        class="form-control form-control-sm"
        type="datetime-local"
        :value="model.created_to?.slice(0, 16)"
        @change="
          updateInstant('created_to', ($event.target as HTMLInputElement).value)
        "
    /></label>
    <button
      class="btn btn-sm btn-outline-secondary align-self-end"
      :disabled="!hasFilters"
      @click="clear"
    >
      Clear filters
    </button>
  </section>
</template>
<script setup lang="ts">
import MultiSelect from "primevue/multiselect";
import { computed } from "vue";
import CompanySearchDropdown from "@/components/common/CompanySearchDropdown.vue";
import AccountSearchDropdown from "@/components/common/AccountSearchDropdown.vue";
import type { ActivityLogFilters } from "./activityLogTypes";

type IdKey = "order_id" | "portfolio_id" | "sales_transaction_id";

const props = defineProps<{ activityKeys: string[] }>();
const model = defineModel<ActivityLogFilters>({ required: true });

const idFields: { key: IdKey; label: string }[] = [
  { key: "order_id", label: "Order ID" },
  { key: "portfolio_id", label: "Portfolio ID" },
  { key: "sales_transaction_id", label: "Sales transaction ID" },
];

// A key restored from the URL stays selectable even before (or without) the key list loading.
const keyOptions = computed(() => [
  ...new Set([...props.activityKeys, ...(model.value.activity_keys ?? [])]),
]);
const hasFilters = computed(() =>
  Object.values(model.value).some((value) => value !== undefined)
);

function update(patch: ActivityLogFilters) {
  model.value = { ...model.value, ...patch };
}
function updateId(key: IdKey, value: string) {
  const id = Number(value);
  update({ [key]: Number.isSafeInteger(id) && id > 0 ? id : undefined });
}
function updateInstant(key: "created_from" | "created_to", value: string) {
  update({ [key]: value ? new Date(`${value}Z`).toISOString() : undefined });
}
function clear() {
  model.value = {};
}
</script>
<style scoped lang="scss">
.filter-bar {
  display: grid;
  grid-template-columns: repeat(3, minmax(200px, 1fr));
  gap: 0.75rem;
  align-items: center;
}
.filter-bar > * {
  min-width: 0;
}
@media (max-width: 900px) {
  .filter-bar {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
